import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuthStore } from '../store/useAuthStore'

const ProjectManagementPage = () => {
  const { id } = useParams()
  const { profile } = useAuthStore()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')

  // Mock project data
  useEffect(() => {
    const mockProject = {
      id: id,
      title: "E-commerce Website Development",
      description: "Building a complete e-commerce solution with modern design and payment integration.",
      status: "active",
      budget: 150000,
      currency: "BDT",
      startDate: "2024-01-15",
      dueDate: "2024-03-15",
      progress: 75,
      client: {
        name: "John Doe",
        email: "john@example.com",
        phone: "+880 1234 567890"
      },
      team: [
        { id: 1, name: "Alice Johnson", role: "Project Manager", avatar: "AJ" },
        { id: 2, name: "Bob Smith", role: "Developer", avatar: "BS" },
        { id: 3, name: "Carol Davis", role: "Designer", avatar: "CD" }
      ],
      milestones: [
        { id: 1, title: "Design Phase", status: "completed", dueDate: "2024-01-30", amount: 30000 },
        { id: 2, title: "Development Phase", status: "in_progress", dueDate: "2024-02-28", amount: 80000 },
        { id: 3, title: "Testing Phase", status: "pending", dueDate: "2024-03-10", amount: 40000 }
      ],
      tasks: [
        { id: 1, title: "Create wireframes", assignee: "Carol Davis", status: "completed", priority: "high" },
        { id: 2, title: "Design UI mockups", assignee: "Carol Davis", status: "completed", priority: "high" },
        { id: 3, title: "Setup database", assignee: "Bob Smith", status: "completed", priority: "medium" },
        { id: 4, title: "Implement user authentication", assignee: "Bob Smith", status: "in_progress", priority: "high" },
        { id: 5, title: "Create product catalog", assignee: "Bob Smith", status: "pending", priority: "medium" },
        { id: 6, title: "Integrate payment gateway", assignee: "Bob Smith", status: "pending", priority: "high" }
      ],
      files: [
        { id: 1, name: "Design Mockups.pdf", size: "2.4 MB", uploadedBy: "Carol Davis", uploadedAt: "2024-01-20" },
        { id: 2, name: "Database Schema.sql", size: "156 KB", uploadedBy: "Bob Smith", uploadedAt: "2024-01-25" },
        { id: 3, name: "Project Requirements.docx", size: "1.2 MB", uploadedBy: "Alice Johnson", uploadedAt: "2024-01-15" }
      ],
      messages: [
        { id: 1, user: "Alice Johnson", message: "Great progress on the design phase!", timestamp: "2024-01-20 10:30" },
        { id: 2, user: "Bob Smith", message: "Database setup completed successfully", timestamp: "2024-01-25 14:15" },
        { id: 3, user: "Carol Davis", message: "UI mockups are ready for review", timestamp: "2024-01-28 16:45" }
      ]
    }
    
    setTimeout(() => {
      setProject(mockProject)
      setLoading(false)
    }, 1000)
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="page-container py-8">
          <div className="animate-pulse">
            <div className="bg-gray-200 h-8 w-64 rounded mb-4"></div>
            <div className="bg-gray-200 h-4 w-96 rounded mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-gray-200 h-32 rounded"></div>
                <div className="bg-gray-200 h-32 rounded"></div>
              </div>
              <div className="bg-gray-200 h-64 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="page-container py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h1>
            <Link to="/admin" className="btn-primary">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    )
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="page-container py-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.title}</h1>
              <p className="text-gray-600 mb-4">{project.description}</p>
              <div className="flex items-center space-x-6 text-sm text-gray-600">
                <span>Budget: ৳{project.budget.toLocaleString()}</span>
                <span>Due: {new Date(project.dueDate).toLocaleDateString()}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                  {project.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Progress</div>
              <div className="text-2xl font-bold text-primary-600">{project.progress}%</div>
              <div className="w-32 bg-gray-200 rounded-full h-2 mt-2">
                <div className="bg-primary-600 h-2 rounded-full" style={{width: `${project.progress}%`}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="page-container">
          <nav className="flex space-x-8">
            {['overview', 'tasks', 'milestones', 'files', 'chat'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="page-container py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Project Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="card">
                <h2 className="text-xl font-semibold mb-4">Project Information</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Start Date</label>
                    <p className="text-gray-900">{new Date(project.startDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Due Date</label>
                    <p className="text-gray-900">{new Date(project.dueDate).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Budget</label>
                    <p className="text-gray-900">৳{project.budget.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                      {project.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h2 className="text-xl font-semibold mb-4">Client Information</h2>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {project.client.name}</p>
                  <p><span className="font-medium">Email:</span> {project.client.email}</p>
                  <p><span className="font-medium">Phone:</span> {project.client.phone}</p>
                </div>
              </div>
            </div>

            {/* Team Members */}
            <div className="card">
              <h2 className="text-xl font-semibold mb-4">Team Members</h2>
              <div className="space-y-3">
                {project.team.map((member) => (
                  <div key={member.id} className="flex items-center">
                    <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white text-sm font-medium">{member.avatar}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-sm text-gray-600">{member.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="card">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Tasks</h2>
              <button className="btn-primary">Add Task</button>
            </div>
            <div className="space-y-4">
              {project.tasks.map((task) => (
                <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium">{task.title}</h3>
                    <div className="flex space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                        {task.priority.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(task.status)}`}>
                        {task.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Assigned to: {task.assignee}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'milestones' && (
          <div className="card">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Milestones</h2>
              <button className="btn-primary">Add Milestone</button>
            </div>
            <div className="space-y-4">
              {project.milestones.map((milestone) => (
                <div key={milestone.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium">{milestone.title}</h3>
                    <div className="flex space-x-2">
                      <span className="text-sm text-gray-600">৳{milestone.amount.toLocaleString()}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(milestone.status)}`}>
                        {milestone.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">Due: {new Date(milestone.dueDate).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'files' && (
          <div className="card">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Files</h2>
              <button className="btn-primary">Upload File</button>
            </div>
            <div className="space-y-3">
              {project.files.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-gray-600">{file.size} • Uploaded by {file.uploadedBy}</p>
                    </div>
                  </div>
                  <button className="text-primary-600 hover:text-primary-700 text-sm">Download</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Project Chat</h2>
            <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
              {project.messages.map((message) => (
                <div key={message.id} className="flex items-start">
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white text-xs font-medium">
                      {message.user.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="text-sm font-medium">{message.user}</p>
                      <p className="text-xs text-gray-500">{message.timestamp}</p>
                    </div>
                    <p className="text-sm text-gray-600">{message.message}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="Type a message..."
                className="flex-1 input-field"
              />
              <button className="btn-primary">Send</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectManagementPage
